/**
 * @fileoverview Confirm payment dialog for a My Page target.
 * @description Opened by a Super Admin from the details of a target whose payment details were sent.
 * Confirming completes the target and makes the bank's prospect Live, and cannot be undone, so the
 * dialog states that before asking. Saves through Confirm Payment with the version the details
 * showed as `If-Match` (and needs a recent MFA sign-in); a target that changed meanwhile is
 * refused and shown for review. The parent mounts it only while it is open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useConfirmMyPagePayment } from '@/features/sales/hooks/useConfirmMyPagePayment'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { isStaleMyPageError } from '@/features/sales/lib/my-page-stale.utils'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { MyPageTargetDetails } from '@/features/sales/types/my-page.types'

interface MyPageConfirmPaymentDialogProps {
  target: MyPageTargetDetails
  onClose: () => void
}

/**
 * @component
 * @param {MyPageConfirmPaymentDialogProps} props - The target and the close handler.
 * @param {MyPageTargetDetails} props.target - The target as the details last showed it.
 * @param {() => void} props.onClose - Called when the dialog is dismissed or the payment confirmed.
 */
function MyPageConfirmPaymentDialog({ target, onClose }: MyPageConfirmPaymentDialogProps) {
  const { t } = useTranslation()
  const confirm = useConfirmMyPagePayment(onClose)
  const refreshSales = useRefreshSalesQueries()
  const close = useGuardedClose(onClose, confirm.isPending)
  const isStale = confirm.isError && isStaleMyPageError(confirm.error)

  /**
   * @description Confirms the payment against the version the details showed.
   */
  const onConfirm = () => {
    confirm.mutate({ targetId: target.targetId, version: target.version })
  }

  /**
   * @description Reloads the latest state and returns to the details for review.
   */
  const onRefresh = () => {
    refreshSales()
    onClose()
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="my-page-confirm-payment-dialog"
      title={t('sales.myWork.dialogs.confirmPayment.title')}
      subtitle={target.bankName}
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13.5px] text-ink">
          {t('sales.myWork.dialogs.confirmPayment.description')}
        </p>
        <p
          className="text-[13px] font-medium text-warning-text"
          data-testid="my-page-confirm-warning"
        >
          {t('sales.myWork.dialogs.confirmPayment.warning')}
        </p>
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={false} />}
        {confirm.isError && !isStale && <SalesRequestError error={confirm.error} />}
        <DialogActions
          testIdPrefix="my-page-confirm-payment"
          cancelLabel={t('sales.myWork.dialogs.cancel')}
          confirmLabel={t('sales.myWork.dialogs.confirmPayment.submit')}
          onCancel={close}
          onConfirm={onConfirm}
          isLoading={confirm.isPending}
          disabled={isStale}
        />
      </div>
    </Modal>
  )
}

export default MyPageConfirmPaymentDialog
